import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, GitBranch, GitCommit, GitMerge, GitPullRequest, Star, Users, Package, Mic, Building2, Trophy, Sparkles, TrendingUp, Calendar, MapPin, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// Font loader
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');

    .font-display {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }
    .font-mono {
      font-family: 'JetBrains Mono', monospace;
    }
  `}</style>
);

// GitHub contribution graph colors
const contributionColors = {
  level0: '#161b22',
  level1: '#0e4429',
  level2: '#006d32',
  level3: '#26a641',
  level4: '#39d353',
};

// Timeline data
const timeline = [
  {
    year: '2018',
    title: 'The First PR',
    subtitle: 'FOSSASIA Membership',
    description: 'Awarded FOSSASIA membership. First taste of global open source community.',
    icon: <GitPullRequest className="w-5 h-5" />,
    color: contributionColors.level1,
  },
  {
    year: '2020',
    title: 'Google Summer of Code',
    subtitle: 'Submitty - Peer Grading',
    description: 'Built the peer grading system for a 720★ educational platform. Mentored by Barbara Cutler at RPI.',
    icon: <Trophy className="w-5 h-5" />,
    color: contributionColors.level2,
    link: 'https://submitty.org/developer/google_summer_of_code/2020_Harsh_Joshi',
  },
  {
    year: '2021',
    title: '100LinesOfCode',
    subtitle: 'The Constraint Experiment',
    description: 'Started a repo with one rule: every project under 100 lines. It would grow to 717★.',
    icon: <GitCommit className="w-5 h-5" />,
    color: contributionColors.level2,
    link: 'https://github.com/josharsh/100LinesOfCode',
  },
  {
    year: '2021',
    title: 'Founding Engineer',
    subtitle: 'Plum Health-Tech',
    description: 'Joined as employee #10. Open source thinking shaped how we built for scale.',
    icon: <Building2 className="w-5 h-5" />,
    color: contributionColors.level3,
  },
  {
    year: '2024',
    title: 'Conference Speaker',
    subtitle: 'CloudX & DeveloperWeek',
    description: 'Speaking at major conferences. The repos became the credibility.',
    icon: <Mic className="w-5 h-5" />,
    color: contributionColors.level3,
  },
  {
    year: '2024',
    title: 'MCP Ecosystem',
    subtitle: 'First-Mover Advantage',
    description: 'Built 4+ tools for Model Context Protocol when it was brand new. mcp-jest became the testing standard.',
    icon: <Package className="w-5 h-5" />,
    color: contributionColors.level4,
  },
  {
    year: '2025',
    title: 'Visa',
    subtitle: 'Payment Systems at Scale',
    description: 'Senior Engineer building global payment infrastructure. The journey continues.',
    icon: <Sparkles className="w-5 h-5" />,
    color: contributionColors.level4,
  },
];

// Stats data
const stats = [
  { label: 'GitHub Stars', value: '1,600+', icon: <Star className="w-5 h-5" /> },
  { label: 'Contributors', value: '200+', icon: <Users className="w-5 h-5" /> },
  { label: 'Repositories', value: '152', icon: <GitBranch className="w-5 h-5" /> },
  { label: 'npm/PyPI Packages', value: '8+', icon: <Package className="w-5 h-5" /> },
];

// Projects showcase
const projects = [
  {
    name: '100LinesOfCode',
    stars: '717',
    forks: '332',
    description: 'Proof that constraints breed creativity',
    link: 'https://github.com/josharsh/100LinesOfCode',
  },
  {
    name: 'OPython-Init',
    stars: '165',
    forks: '89',
    description: 'Object-oriented Python for beginners',
    link: 'https://github.com/josharsh/OPython-Init',
  },
  {
    name: 'mcp-jest',
    stars: '50+',
    forks: '10+',
    description: 'The first MCP testing framework',
    link: 'https://github.com/josharsh/mcp-jest',
  },
  {
    name: 'Paper',
    stars: '56',
    forks: '20+',
    description: 'Document scanning with ML',
    link: 'https://github.com/josharsh/Paper',
  },
];

const OpenSourcePlaybookStudy = () => {
  const navigate = useNavigate();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((scrolled / maxScroll) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-advance timeline
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTimelineIndex((prev) => (prev + 1) % timeline.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#0d1117] text-[#c9d1d9]">
      <FontLoader />

      {/* Progress bar - GitHub green */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#21262d] z-50">
        <motion.div
          className="h-full"
          style={{
            width: `${scrollProgress}%`,
            background: `linear-gradient(90deg, ${contributionColors.level1}, ${contributionColors.level4})`
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#0d1117]/90 backdrop-blur-sm border-b border-[#21262d]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/labs')}
            className="flex items-center gap-2 text-[#8b949e] hover:text-white transition-colors font-mono text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>cd ../labs</span>
          </motion.button>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            href="https://github.com/josharsh"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#8b949e] hover:text-white transition-colors text-sm"
          >
            <GitBranch className="w-4 h-4" />
            <span>@josharsh</span>
          </motion.a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* The Question */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <span className="font-mono text-[#8b949e] text-sm">
              // the question everyone asks
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-[1.1]"
          >
            Is Open Source
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#238636] to-[#39d353]">
              Actually Worth It?
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-[#8b949e] mb-12 max-w-2xl mx-auto"
          >
            7 years. 1,600+ stars. GSoC. Conference stages. Visa.
            <br />
            <span className="text-white">Here's what I learned.</span>
          </motion.p>

          {/* Quick stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-8 mb-16"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="flex items-center justify-center gap-2 text-[#39d353] mb-1">
                  {stat.icon}
                  <span className="font-mono text-2xl font-bold">{stat.value}</span>
                </div>
                <p className="text-[#8b949e] text-sm">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-[#484f58] font-mono text-xs"
          >
            ↓ scroll for the story
          </motion.div>
        </div>
      </section>

      {/* The Doubt Section */}
      <section className="py-24 px-6 border-t border-[#21262d]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">01 // THE DOUBT</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">
              "Why would I give away my best work for free?"
            </h2>

            <div className="space-y-6 text-lg text-[#8b949e]">
              <p>
                Every developer asks this question. I asked it too.
              </p>
              <p>
                In 2018, I was a college student in India. No connections. No brand name on my resume.
                Just code and curiosity.
              </p>
              <p>
                Someone told me: <span className="text-white">"Contribute to open source."</span>
              </p>
              <p>
                It sounded like terrible career advice. Work for free? Give away intellectual property?
                Spend nights debugging someone else's project?
              </p>
              <p className="text-white font-medium">
                Seven years later, I understand: they weren't asking me to give.
                They were showing me how to invest.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Journey Timeline */}
      <section className="py-24 px-6 bg-[#161b22]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">02 // THE JOURNEY</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              7 Years of Compound Interest
            </h2>
            <p className="text-[#8b949e] mb-12 max-w-2xl">
              Each commit built on the last. Each project opened new doors. Here's the timeline.
            </p>

            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[27px] top-0 bottom-0 w-px bg-[#21262d]" />

              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <motion.div
                    key={item.year + item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative flex gap-6"
                  >
                    {/* Icon */}
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                      style={{ backgroundColor: item.color }}
                    >
                      {item.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-[#39d353] text-sm">{item.year}</span>
                        <span className="text-[#484f58]">•</span>
                        <span className="text-[#8b949e] text-sm">{item.subtitle}</span>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#8b949e] mb-3">{item.description}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#58a6ff] hover:underline text-sm"
                        >
                          View <ArrowUpRight className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Compound Effect */}
      <section className="py-24 px-6 border-t border-[#21262d]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">03 // THE COMPOUND EFFECT</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">
              How Each Project Built on the Last
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {/* Connection diagram */}
              <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-6">
                <h3 className="font-mono text-[#39d353] text-sm mb-4">// the chain reaction</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <GitCommit className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      <span className="text-white">FOSSASIA</span> taught me how communities work
                    </p>
                  </div>
                  <div className="flex items-start gap-3 ml-4">
                    <GitMerge className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      Which helped me get into <span className="text-white">GSoC</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 ml-8">
                    <GitMerge className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      Which taught me to build <span className="text-white">100LinesOfCode</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 ml-12">
                    <GitMerge className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      Which attracted <span className="text-white">200+ contributors</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 ml-16">
                    <GitMerge className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      Which built credibility for <span className="text-white">conferences</span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3 ml-20">
                    <Star className="w-4 h-4 text-[#39d353] mt-1 flex-shrink-0" />
                    <p className="text-[#8b949e]">
                      Which led to <span className="text-white">Visa</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* The insight */}
              <div className="bg-gradient-to-br from-[#238636]/20 to-[#0d1117] border border-[#238636]/30 rounded-lg p-6">
                <h3 className="font-mono text-[#39d353] text-sm mb-4">// the insight</h3>
                <p className="text-xl text-white font-medium mb-4">
                  Open source isn't charity.
                  <br />
                  It's compound interest.
                </p>
                <p className="text-[#8b949e]">
                  Every contribution is a deposit. The interest compounds through:
                  reputation, connections, opportunities, and skills.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Portfolio */}
      <section className="py-24 px-6 bg-[#161b22]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">04 // THE PORTFOLIO</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Projects That Compound
            </h2>
            <p className="text-[#8b949e] mb-12">
              Each one started small. Each one grew through community.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {projects.map((project, index) => (
                <motion.a
                  key={project.name}
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group block bg-[#0d1117] border border-[#21262d] rounded-lg p-6 hover:border-[#388bfd] transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-mono text-[#58a6ff] group-hover:underline">
                      {project.name}
                    </h3>
                    <ArrowUpRight className="w-4 h-4 text-[#484f58] group-hover:text-[#58a6ff] transition-colors" />
                  </div>
                  <p className="text-[#8b949e] text-sm mb-4">{project.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-[#8b949e]">
                      <Star className="w-4 h-4" /> {project.stars}
                    </span>
                    <span className="flex items-center gap-1 text-[#8b949e]">
                      <GitBranch className="w-4 h-4" /> {project.forks}
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* More on GitHub */}
            <div className="mt-8 text-center">
              <a
                href="https://github.com/josharsh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#58a6ff] hover:underline"
              >
                View all 152 repositories on GitHub
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Unexpected Returns */}
      <section className="py-24 px-6 border-t border-[#21262d]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">05 // THE UNEXPECTED RETURNS</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">
              What I Didn't Expect
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-6">
                <Mic className="w-8 h-8 text-[#39d353] mb-4" />
                <h3 className="font-bold text-white mb-2">Conference Stages</h3>
                <p className="text-[#8b949e] text-sm">
                  CloudX 2024, DeveloperWeek Global. The repos became my resume.
                </p>
              </div>

              <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-6">
                <Users className="w-8 h-8 text-[#39d353] mb-4" />
                <h3 className="font-bold text-white mb-2">Global Network</h3>
                <p className="text-[#8b949e] text-sm">
                  Contributors from 20+ countries. Mentors who became friends.
                </p>
              </div>

              <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-6">
                <TrendingUp className="w-8 h-8 text-[#39d353] mb-4" />
                <h3 className="font-bold text-white mb-2">Career Leverage</h3>
                <p className="text-[#8b949e] text-sm">
                  From college in India to Visa. Open source was the bridge.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Answer */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0d1117] to-[#161b22]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">06 // THE ANSWER</p>

            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-8">
              Is Open Source Worth It?
            </h2>

            <div className="bg-[#161b22] border border-[#238636] rounded-lg p-8 md:p-12 mb-12">
              <p className="text-2xl md:text-3xl text-white font-medium mb-6">
                Yes. But not for the reasons you think.
              </p>
              <div className="text-left space-y-4 text-[#8b949e]">
                <p>
                  <span className="text-white">It's not about the stars.</span> It's about what you learn maintaining a project used by others.
                </p>
                <p>
                  <span className="text-white">It's not about the downloads.</span> It's about the problems you solve that make you a better engineer.
                </p>
                <p>
                  <span className="text-white">It's not about the recognition.</span> It's about the compound effect of every contribution building on the last.
                </p>
              </div>
            </div>

            <p className="text-xl text-[#8b949e] mb-8">
              The best career advice I ever got was to give my work away.
              <br />
              <span className="text-white">Seven years later, I'm still collecting the returns.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Playbook */}
      <section className="py-24 px-6 border-t border-[#21262d]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">07 // THE PLAYBOOK</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">
              How to Start
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#238636] flex items-center justify-center flex-shrink-0 font-mono text-sm">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Start with your own itch</h3>
                  <p className="text-[#8b949e]">
                    Build something you need. 100LinesOfCode started because I wanted to prove constraints work.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#238636] flex items-center justify-center flex-shrink-0 font-mono text-sm">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Contribute to projects you use</h3>
                  <p className="text-[#8b949e]">
                    GSoC came from contributing to Submitty. Use something? Improve it.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#238636] flex items-center justify-center flex-shrink-0 font-mono text-sm">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Be early to ecosystems</h3>
                  <p className="text-[#8b949e]">
                    MCP was new. I built the testing framework. First-mover advantage is real.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[#238636] flex items-center justify-center flex-shrink-0 font-mono text-sm">
                  4
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">Let it compound</h3>
                  <p className="text-[#8b949e]">
                    Don't expect returns in month 1. Think in years. The interest compounds.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#161b22] border-t border-[#21262d]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-mono text-[#39d353] text-sm mb-6">// your turn</p>

            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
              Start Your Compound Journey
            </h2>

            <p className="text-[#8b949e] mb-8 max-w-xl mx-auto">
              The best time to start was 7 years ago.
              The second best time is today.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://github.com/josharsh"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-[#238636] hover:bg-[#2ea043] text-white font-medium rounded-md transition-colors flex items-center gap-2"
              >
                <GitBranch className="w-5 h-5" />
                Follow on GitHub
              </a>
              <a
                href="https://github.com/josharsh/100LinesOfCode"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-[#30363d] hover:border-[#8b949e] text-[#c9d1d9] font-medium rounded-md transition-colors flex items-center gap-2"
              >
                <Star className="w-5 h-5" />
                Star 100LinesOfCode
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#21262d]">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#8b949e]">
          <p>
            Written by{' '}
            <a href="https://twitter.com/AKJosh21" className="text-[#58a6ff] hover:underline">
              @josharsh
            </a>
          </p>
          <div className="flex items-center gap-4">
            <a href="https://github.com/josharsh" className="hover:text-white transition-colors">
              GitHub
            </a>
            <a href="https://www.linkedin.com/in/josharsh/" className="hover:text-white transition-colors">
              LinkedIn
            </a>
            <a href="https://sessionize.com/josharsh/" className="hover:text-white transition-colors">
              Speaking
            </a>
          </div>
        </div>
      </footer>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="open-source-playbook" variant="dark" />
    </div>
  );
};

export default OpenSourcePlaybookStudy;
